/**
 * Onglet « Bottin de réinscription » — L1 (S1-S2) / L2 (S1-S4), groupé par
 * option. Aperçu à l'écran + impression A4 paysage (en-tête 2i + signature
 * chef de la scolarité). 2026-10-15.
 */
import React, { useState, useCallback, useMemo } from 'react';
import { FileText, Printer } from 'lucide-react';
import { useLazyGetDonneesBottinReinscriptionQuery } from '../../services/pvDeliberationApi';
import { useGetPolesQuery } from '../../services/polesOptionsApi';
import { buildBottinReinscriptionHTML, type DonneesBottinReinscription } from './BottinReinscriptionBuilder';

const BottinReinscriptionTab: React.FC<{ annees: string[]; defaultAnnee: string }> = ({ annees, defaultAnnee }) => {
  const [niveau, setNiveau] = useState<'L1' | 'L2'>('L1');
  const [annee, setAnnee] = useState(defaultAnnee);
  const [campus, setCampus] = useState('Pointe-Noire');
  const [chef, setChef] = useState('');
  const [seuil, setSeuil] = useState(10);
  const [dateEmission, setDateEmission] = useState('');
  const [poleFiltre, setPoleFiltre] = useState(''); // '' = tous les pôles
  const [fetchData, { data, isFetching, error }] = useLazyGetDonneesBottinReinscriptionQuery();
  const [feedback, setFeedback] = useState<string | null>(null);

  // Liste des pôles (toujours disponible, indépendante du chargement) pour que
  // le filtre soit cliquable avant même d'avoir chargé le bottin.
  const { data: polesList = [] } = useGetPolesQuery({});
  const polesDispo = useMemo(
    () => Array.from(new Set((polesList as any[]).map(p => p.nom).filter(Boolean) as string[])).sort(),
    [polesList]);

  // Groupes après filtre pôle (affichage + impression).
  const groupesAffichees = useMemo(() => {
    if (!data) return [];
    return poleFiltre ? data.groupes.filter(g => g.pole === poleFiltre) : data.groupes;
  }, [data, poleFiltre]);

  const effectifAff = useMemo(() => groupesAffichees.reduce((s, g) => s + g.effectif, 0), [groupesAffichees]);
  const eligiblesAff = useMemo(() => groupesAffichees.reduce((s, g) => s + g.eligibles, 0), [groupesAffichees]);

  const charger = useCallback(async () => {
    setFeedback(null);
    setPoleFiltre(''); // repartir sur « Tous les pôles » à chaque rechargement
    try {
      await fetchData({ niveau, annee_academique: annee, campus, responsable_academique: chef, seuil_eligibilite: seuil }).unwrap();
    } catch (e: any) {
      setFeedback(e?.data?.error || 'Erreur de chargement');
    }
  }, [fetchData, niveau, annee, campus, chef, seuil]);

  const buildHtml = useCallback((d: DonneesBottinReinscription) =>
    buildBottinReinscriptionHTML(d, { ville: campus, chefScolarite: chef, dateEmission }),
    [campus, chef, dateEmission]);

  const imprimer = () => {
    if (!data) return;
    // On imprime uniquement les groupes filtrés (pôle), avec totaux réajustés.
    const dataImpr: DonneesBottinReinscription = {
      ...data,
      groupes: groupesAffichees,
      effectif_total: effectifAff,
      eligibles_total: eligiblesAff,
    };
    const w = window.open('', '_blank');
    if (!w) { alert("Le navigateur a bloque l'ouverture. Autorisez les popups."); return; }
    w.document.open(); w.document.write(buildHtml(dataImpr)); w.document.close();
    setTimeout(() => { try { w.focus(); w.print(); } catch { /* noop */ } }, 400);
  };

  return (
    <div className="space-y-5">
      <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-700 rounded-lg p-3 flex items-start gap-3">
        <FileText size={18} className="text-indigo-600 dark:text-indigo-400 mt-0.5 flex-shrink-0" />
        <div className="text-sm">
          <p className="font-semibold text-indigo-800 dark:text-indigo-300">Bottin de reinscription</p>
          <p className="text-xs text-indigo-700 dark:text-indigo-400 mt-1">
            Tableau par niveau groupe par option, pour verifier l'eligibilite avant reinscription.
            L1 = moyennes S1-S2 ; L2 = moyennes S1-S4 (les semestres L1 sont repris de l'annee precedente). En-tete 2i + signature du chef de la scolarite.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Niveau</label>
          <select value={niveau} onChange={e => setNiveau(e.target.value as 'L1' | 'L2')}
            className="w-full px-2 py-2 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700">
            <option value="L1">L1 (S1-S2)</option>
            <option value="L2">L2 (S1-S4)</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Annee</label>
          <select value={annee} onChange={e => setAnnee(e.target.value)}
            className="w-full px-2 py-2 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700">
            {annees.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Campus</label>
          <input value={campus} onChange={e => setCampus(e.target.value)}
            className="w-full px-2 py-2 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700" />
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Chef de la scolarite</label>
          <input value={chef} onChange={e => setChef(e.target.value)} placeholder="Nom et prenoms"
            className="w-full px-2 py-2 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Seuil eligibilite</label>
          <input type="number" step="0.5" value={seuil} onChange={e => setSeuil(parseFloat(e.target.value) || 10)}
            className="w-full px-2 py-2 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Pole</label>
          <select value={poleFiltre} onChange={e => setPoleFiltre(e.target.value)} disabled={polesDispo.length === 0}
            className="w-full px-2 py-2 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 disabled:opacity-50">
            <option value="">Tous les poles</option>
            {polesDispo.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <input type="date" value={dateEmission} onChange={e => setDateEmission(e.target.value)}
          className="px-2 py-2 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700" />
        <button onClick={charger} disabled={isFetching}
          className="px-4 py-2 text-sm rounded bg-blue-600 hover:bg-blue-700 text-white font-medium disabled:opacity-50">
          {isFetching ? 'Chargement...' : 'Charger'}
        </button>
        <button onClick={imprimer} disabled={!data || isFetching}
          className="px-4 py-2 text-sm rounded bg-emerald-600 hover:bg-emerald-700 text-white font-medium disabled:opacity-50 inline-flex items-center gap-2">
          <Printer size={15} /> Imprimer
        </button>
      </div>

      {(feedback || error) && (
        <div className="text-sm text-red-600 dark:text-red-400">{feedback || 'Erreur de chargement'}</div>
      )}

      {data && (
        <div className="space-y-3">
          <div className="flex gap-4 text-sm">
            <span>Effectif : <strong>{effectifAff}</strong></span>
            <span className="text-emerald-600 dark:text-emerald-400">Eligibles : <strong>{eligiblesAff}</strong></span>
            <span>Options : <strong>{groupesAffichees.length}</strong></span>
            {poleFiltre && <span className="text-indigo-600 dark:text-indigo-400">Pole : <strong>{poleFiltre}</strong></span>}
          </div>
          <div className="max-h-[420px] overflow-auto border border-gray-200 dark:border-gray-700 rounded">
            {groupesAffichees.map(g => (
              <div key={g.option} className="border-b border-gray-200 dark:border-gray-700 last:border-0">
                <div className="bg-gray-100 dark:bg-gray-800 px-3 py-1.5 text-sm font-semibold flex justify-between">
                  <span>{g.option}</span>
                  <span className="text-xs text-gray-500">{g.effectif} - {g.eligibles} eligible(s)</span>
                </div>
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 dark:bg-gray-900/40 text-gray-500">
                    <tr>
                      <th className="px-2 py-1 text-left">Matricule</th>
                      <th className="px-2 py-1 text-left">Nom &amp; Prenoms</th>
                      {data.semestres.map(s => <th key={s} className="px-2 py-1">S{s}</th>)}
                      <th className="px-2 py-1">Moy.</th>
                      <th className="px-2 py-1">Elig.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.etudiants.map(e => (
                      <tr key={e.matricule} className="border-t border-gray-100 dark:border-gray-800">
                        <td className="px-2 py-1">{e.matricule}</td>
                        <td className="px-2 py-1"><strong>{e.nom}</strong> {e.prenoms}</td>
                        {data.semestres.map(s => {
                          const v = (e as any)[`moyenne_s${s}`];
                          return <td key={s} className={`px-2 py-1 text-center ${v != null && v < 10 ? 'text-red-600' : ''}`}>{v == null ? '-' : v.toFixed(2)}</td>;
                        })}
                        <td className="px-2 py-1 text-center font-semibold">{e.moyenne_generale == null ? '-' : e.moyenne_generale.toFixed(2)}</td>
                        <td className={`px-2 py-1 text-center font-bold ${e.eligible ? 'text-emerald-600' : 'text-red-600'}`}>{e.eligible ? 'OUI' : 'NON'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BottinReinscriptionTab;
